import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import {
  useFinanceiroStats,
  useFaturas,
  useLancamentos,
  useEmpresas,
} from "@/hooks/use-data";
import type { Fatura, Lancamento } from "@shared/schema";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TaxasAsaasPanel } from "@/components/financeiro/TaxasAsaasPanel";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  CheckCircle2,
  X,
  Wallet,
} from "lucide-react";
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { api } from "@shared/routes";
import {
  FINANCEIRO_STATUS_COLORS,
  FINANCEIRO_STATUS_LABELS,
  FINANCEIRO_CATEGORIAS,
} from "@/lib/constants";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// ─── helpers ──────────────────────────────────────────────────────────────────

function formatBRL(value: number): string {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

const MESES = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

// ─── SummaryCard ──────────────────────────────────────────────────────────────

function SummaryCard({
  label,
  value,
  icon: Icon,
  colorClass,
  isLoading,
}: {
  label: string;
  value: number;
  icon: React.ElementType;
  colorClass: string;
  isLoading: boolean;
}) {
  return (
    <Card className="border-none shadow-md">
      <CardContent className="pt-6">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${colorClass}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            {isLoading ? (
              <Skeleton className="h-7 w-32 mt-1" />
            ) : (
              <p className="text-2xl font-bold text-slate-800">
                {formatBRL(value)}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── CriarFaturaRpc ───────────────────────────────────────────────────────────

function CriarFaturaRpc({
  session,
  onClose,
}: {
  session: any;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const { data: empresas } = useEmpresas();
  const vistoriadoras = (empresas ?? []).filter(
    (e) => e.tipo === "VISTORIADORA",
  );
  const now = new Date();
  const [vid, setVid] = useState("");
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [ano, setAno] = useState(now.getFullYear());
  const [vencimentoDias, setVencimentoDias] = useState(30);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/financeiro/faturas/criar-rpc", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          vistoriadora_id: vid,
          mes,
          ano,
          vencimento_dias: vencimentoDias,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao criar fatura");
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [api.financeiro.faturas.list.path],
      });
      queryClient.invalidateQueries({ queryKey: [api.financeiro.stats.path] });
      toast({
        title: "Fatura criada",
        description: `ID: ${data.faturaId}`,
      });
      onClose();
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  return (
    <Card className="border-2 border-primary/20 shadow-lg mb-6">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <CardTitle className="text-base">Nova Fatura (via RPC)</CardTitle>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-4">
          Agrupa todos os laudos finalizados do período ainda não faturados.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label>Vistoriadora *</Label>
            <Select value={vid} onValueChange={setVid}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a vistoriadora" />
              </SelectTrigger>
              <SelectContent>
                {vistoriadoras.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.nomeFantasia || v.razaoSocial}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Mês de referência *</Label>
            <Select
              value={String(mes)}
              onValueChange={(v) => setMes(Number(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MESES.map((m, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Ano *</Label>
            <Input
              type="number"
              value={ano}
              onChange={(e) => setAno(Number(e.target.value))}
              min={2020}
              max={2099}
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label>Vencimento</Label>
            <Select
              value={String(vencimentoDias)}
              onValueChange={(v) => setVencimentoDias(Number(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 dias</SelectItem>
                <SelectItem value="15">15 dias</SelectItem>
                <SelectItem value="30">30 dias</SelectItem>
                <SelectItem value="45">45 dias</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!vid || mutation.isPending}
          >
            {mutation.isPending && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            Criar Fatura
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── LancamentoForm ───────────────────────────────────────────────────────────

function LancamentoForm({
  lancamento,
  session,
  isOwner,
  empresaId,
  onClose,
}: {
  lancamento?: Lancamento;
  session: any;
  isOwner: boolean;
  empresaId: string;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const { data: empresas } = useEmpresas();
  const vistoriadoras = (empresas ?? []).filter(
    (e) => e.tipo === "VISTORIADORA",
  );

  const isEdit = !!lancamento;
  const today = new Date().toISOString().split("T")[0];

  const [tipo, setTipo] = useState(lancamento?.tipo ?? "receita");
  const [descricao, setDescricao] = useState(lancamento?.descricao ?? "");
  const [valor, setValor] = useState(lancamento?.valor ?? "");
  const [categoria, setCategoria] = useState(lancamento?.categoria ?? "outros");
  const [data, setData] = useState(lancamento?.data ?? today);
  const [vid, setVid] = useState(
    lancamento?.vistoriadoraId ?? (isOwner ? "__none__" : empresaId),
  );

  const mutation = useMutation({
    mutationFn: async (body: Record<string, any>) => {
      const url = isEdit
        ? `/api/financeiro/lancamentos/${lancamento!.id}`
        : "/api/financeiro/lancamentos";
      const method = isEdit ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erro ao salvar lançamento");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.financeiro.lancamentos.list.path],
      });
      queryClient.invalidateQueries({ queryKey: [api.financeiro.stats.path] });
      toast({ title: isEdit ? "Lançamento atualizado" : "Lançamento criado" });
      onClose();
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!descricao || !valor || !data) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha descrição, valor e data.",
        variant: "destructive",
      });
      return;
    }
    mutation.mutate({
      tipo,
      descricao,
      valor,
      categoria,
      data,
      vistoriadoraId: vid === "__none__" ? null : vid || null,
    });
  };

  return (
    <Card className="border-2 border-primary/20 shadow-lg mb-6">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <CardTitle className="text-base">
          {isEdit ? "Editar Lançamento" : "Novo Lançamento"}
        </CardTitle>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="receita">Receita</SelectItem>
                  <SelectItem value="despesa">Despesa</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select value={categoria} onValueChange={setCategoria}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FINANCEIRO_CATEGORIAS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c.charAt(0).toUpperCase() + c.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Descrição *</Label>
              <Input
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Descrição do lançamento"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Valor (R$) *</Label>
              <Input
                type="number"
                step="0.01"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                placeholder="0,00"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Data *</Label>
              <Input
                type="date"
                value={data}
                onChange={(e) => setData(e.target.value)}
                required
              />
            </div>

            {isOwner && (
              <div className="space-y-2">
                <Label>Vistoriadora (opcional)</Label>
                <Select value={vid} onValueChange={setVid}>
                  <SelectTrigger>
                    <SelectValue placeholder="Nenhuma" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nenhuma</SelectItem>
                    {vistoriadoras.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.nomeFantasia || v.razaoSocial}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {isEdit ? "Salvar" : "Criar Lançamento"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Financeiro() {
  const { profile, session } = useAuth();
  const { toast } = useToast();

  const roles = profile?.roles || [];
  const isOwner = roles.includes("owner");
  const empresaId = profile?.empresa?.id ?? profile?.empresaId ?? "";
  const empresaTipo = profile?.empresa?.tipo ?? "";

  const [filterVid, setFilterVid] = useState<string | undefined>(
    isOwner ? undefined : empresaId,
  );
  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [showFaturaForm, setShowFaturaForm] = useState(false);
  const [showLancamentoForm, setShowLancamentoForm] = useState(false);
  const [editingLancamento, setEditingLancamento] = useState<
    Lancamento | undefined
  >();

  const { data: stats, isLoading: statsLoading } =
    useFinanceiroStats(filterVid);
  const { data: faturasData, isLoading: faturasLoading } = useFaturas(
    filterVid,
    undefined,
    undefined,
    filterStatus,
  );
  const { data: lancamentosData, isLoading: lancamentosLoading } =
    useLancamentos(filterVid);
  const { data: empresas } = useEmpresas();
  const vistoriadoras = (empresas ?? []).filter(
    (e) => e.tipo === "VISTORIADORA",
  );

  // Build bar chart data: last 6 months of paid faturas
  const chartData = (() => {
    const result = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const m = d.getMonth() + 1;
      const a = d.getFullYear();
      const total = (faturasData ?? [])
        .filter((f) => f.status === "pago" && f.mes === m && f.ano === a)
        .reduce((sum, f) => sum + parseFloat(f.valorTotal ?? "0"), 0);
      result.push({ mes: `${MESES[m - 1]}/${a.toString().slice(2)}`, total });
    }
    return result;
  })();

  // Mutations
  const marcarPagoMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/financeiro/faturas/${id}/pagar`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ metodo_pagamento: "manual" }),
      });
      if (!res.ok) throw new Error("Erro ao marcar como pago");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.financeiro.faturas.list.path],
      });
      queryClient.invalidateQueries({ queryKey: [api.financeiro.stats.path] });
      toast({ title: "Fatura marcada como paga" });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível atualizar a fatura.",
        variant: "destructive",
      });
    },
  });

  const deleteFaturaMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/financeiro/faturas/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error("Erro ao excluir fatura");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.financeiro.faturas.list.path],
      });
      queryClient.invalidateQueries({ queryKey: [api.financeiro.stats.path] });
      toast({ title: "Fatura excluída" });
    },
  });

  const cancelarFaturaMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/financeiro/faturas/${id}/cancelar`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error("Erro ao cancelar fatura");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.financeiro.faturas.list.path],
      });
      toast({ title: "Fatura cancelada" });
    },
  });

  const deleteLancamentoMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/financeiro/lancamentos/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      if (!res.ok) throw new Error("Erro ao excluir lançamento");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.financeiro.lancamentos.list.path],
      });
      queryClient.invalidateQueries({ queryKey: [api.financeiro.stats.path] });
      toast({ title: "Lançamento excluído" });
    },
  });

  const closeFaturaForm = () => setShowFaturaForm(false);

  const openLancamentoForm = (l?: Lancamento) => {
    setEditingLancamento(l);
    setShowLancamentoForm(true);
  };

  const closeLancamentoForm = () => {
    setEditingLancamento(undefined);
    setShowLancamentoForm(false);
  };

  return (
    <Shell>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
        <div>
          <h1
            className="text-3xl font-bold font-display text-slate-900"
            data-testid="text-page-title"
          >
            Financeiro
          </h1>
          <p className="text-muted-foreground mt-1">
            Faturas e lançamentos financeiros
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center">
          {isOwner && (
            <Select
              value={filterVid ?? "all"}
              onValueChange={(v) => setFilterVid(v === "all" ? undefined : v)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Todas as vistoriadoras" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as vistoriadoras</SelectItem>
                {vistoriadoras.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.nomeFantasia || v.razaoSocial}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select
            value={filterStatus ?? "all"}
            onValueChange={(v) => setFilterStatus(v === "all" ? undefined : v)}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Todos os status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="pendente">Pendente</SelectItem>
              <SelectItem value="pago">Pago</SelectItem>
              <SelectItem value="atrasada">Atrasada</SelectItem>
              <SelectItem value="bloqueada">Bloqueada</SelectItem>
              <SelectItem value="vencido">Vencido</SelectItem>
              <SelectItem value="cancelado">Cancelado</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <SummaryCard
          label="A Receber"
          value={stats?.totalReceber ?? 0}
          icon={DollarSign}
          colorClass="bg-blue-100 text-blue-600"
          isLoading={statsLoading}
        />
        <SummaryCard
          label="Recebido"
          value={stats?.totalRecebido ?? 0}
          icon={TrendingUp}
          colorClass="bg-green-100 text-green-600"
          isLoading={statsLoading}
        />
        <SummaryCard
          label="Em Atraso"
          value={stats?.totalVencido ?? 0}
          icon={AlertCircle}
          colorClass="bg-red-100 text-red-600"
          isLoading={statsLoading}
        />
        <SummaryCard
          label="Despesas"
          value={stats?.totalDespesas ?? 0}
          icon={TrendingDown}
          colorClass="bg-orange-100 text-orange-600"
          isLoading={statsLoading}
        />
      </div>

      {/* Bar Chart */}
      <Card className="border-none shadow-md mb-8">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Receitas recebidas — últimos 6 meses
          </CardTitle>
        </CardHeader>
        <CardContent>
          {faturasLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart
                data={chartData}
                margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`}
                  width={52}
                />
                <Tooltip
                  formatter={(v: number) => [formatBRL(v), "Recebido"]}
                  labelStyle={{ fontWeight: 600 }}
                />
                <Bar dataKey="total" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="faturas">
        <TabsList className="mb-4">
          <TabsTrigger value="faturas">Faturas</TabsTrigger>
          <TabsTrigger value="lancamentos">Lançamentos</TabsTrigger>
          {isOwner && <TabsTrigger value="precos">Preços</TabsTrigger>}
          {isOwner && (
            <TabsTrigger value="taxas-asaas" data-testid="tab-taxas-asaas">
              Taxas Asaas
            </TabsTrigger>
          )}
        </TabsList>

        {/* ── Faturas ── */}
        <TabsContent value="faturas">
          {!showFaturaForm && isOwner && (
            <div className="flex justify-end mb-4">
              <Button size="sm" onClick={() => setShowFaturaForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nova Fatura
              </Button>
            </div>
          )}

          {showFaturaForm && isOwner && (
            <CriarFaturaRpc session={session} onClose={closeFaturaForm} />
          )}

          {faturasLoading ? (
            <div className="space-y-3">
              {Array(3)
                .fill(0)
                .map((_, i) => (
                  <Skeleton key={i} className="h-16 rounded-xl" />
                ))}
            </div>
          ) : !faturasData?.length ? (
            <div className="text-center py-16 text-muted-foreground">
              <Wallet className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              Nenhuma fatura encontrada
            </div>
          ) : (
            <div className="space-y-3">
              {faturasData.map((f) => {
                const vid = vistoriadoras.find(
                  (v) => v.id === f.vistoriadoraId,
                );
                return (
                  <Card key={f.id} className="border-none shadow-sm">
                    <CardContent className="py-4">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-mono text-slate-400">
                              {f.numero}
                            </span>
                            <Badge
                              variant="secondary"
                              className={
                                FINANCEIRO_STATUS_COLORS[f.status ?? "pendente"]
                              }
                            >
                              {FINANCEIRO_STATUS_LABELS[f.status ?? "pendente"]}
                            </Badge>
                          </div>
                          <p className="font-medium text-slate-800 mt-0.5">
                            {vid?.nomeFantasia || vid?.razaoSocial || "—"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {MESES[(f.mes ?? 1) - 1]}/{f.ano}
                            {" · "} Vence: {f.vencimento}
                            {(f.diasAtraso ?? 0) > 0
                              ? ` · ${f.diasAtraso}d atraso`
                              : ""}
                          </p>
                          {(f.multa || f.juros) && (
                            <p className="text-xs text-orange-600 mt-0.5">
                              Encargos:{" "}
                              {formatBRL(
                                parseFloat(f.multa ?? "0") +
                                  parseFloat(f.juros ?? "0"),
                              )}
                            </p>
                          )}
                        </div>

                        <div className="text-right shrink-0">
                          <p className="text-lg font-bold text-slate-800">
                            {formatBRL(parseFloat(f.valorTotal ?? "0"))}
                          </p>
                          {f.pagoEm && (
                            <p className="text-xs text-green-600">
                              Pago em{" "}
                              {new Date(f.pagoEm).toLocaleDateString("pt-BR")}
                            </p>
                          )}
                        </div>

                        <div className="flex gap-1 shrink-0">
                          {isOwner &&
                            !f.status?.match(/^(pago|cancelado)$/) && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="w-8 h-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                title="Marcar como pago"
                                disabled={marcarPagoMutation.isPending}
                                onClick={() => marcarPagoMutation.mutate(f.id)}
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                            )}
                          {isOwner && f.status !== "cancelado" && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="w-8 h-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  title="Cancelar fatura"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Cancelar fatura?
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    A fatura <strong>#{f.numero}</strong> será
                                    marcada como cancelada.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Voltar</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-destructive hover:bg-destructive/90"
                                    onClick={() =>
                                      cancelarFaturaMutation.mutate(f.id)
                                    }
                                  >
                                    Cancelar Fatura
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── Lançamentos ── */}
        <TabsContent value="lancamentos">
          {!showLancamentoForm && isOwner && (
            <div className="flex justify-end mb-4">
              <Button size="sm" onClick={() => openLancamentoForm()}>
                <Plus className="w-4 h-4 mr-2" />
                Novo Lançamento
              </Button>
            </div>
          )}

          {showLancamentoForm && (
            <LancamentoForm
              lancamento={editingLancamento}
              session={session}
              isOwner={isOwner}
              empresaId={empresaId}
              onClose={closeLancamentoForm}
            />
          )}

          {lancamentosLoading ? (
            <div className="space-y-3">
              {Array(3)
                .fill(0)
                .map((_, i) => (
                  <Skeleton key={i} className="h-14 rounded-xl" />
                ))}
            </div>
          ) : !lancamentosData?.length ? (
            <div className="text-center py-16 text-muted-foreground">
              <Wallet className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              Nenhum lançamento encontrado
            </div>
          ) : (
            <div className="space-y-3">
              {lancamentosData.map((l) => (
                <Card key={l.id} className="border-none shadow-sm">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-2 h-10 rounded-full flex-shrink-0 ${
                          l.tipo === "receita" ? "bg-green-400" : "bg-red-400"
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">
                          {l.descricao}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {l.data} · {l.categoria}
                          {l.tipo === "receita" ? " · Receita" : " · Despesa"}
                        </p>
                      </div>
                      <p
                        className={`font-bold text-base shrink-0 ${
                          l.tipo === "receita"
                            ? "text-green-700"
                            : "text-red-600"
                        }`}
                      >
                        {l.tipo === "despesa" ? "−" : "+"}
                        {formatBRL(parseFloat(l.valor ?? "0"))}
                      </p>
                      <div className="flex gap-1 shrink-0">
                        {isOwner && (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="w-8 h-8 text-slate-500 hover:text-slate-700"
                              onClick={() => openLancamentoForm(l)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="w-8 h-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Excluir lançamento?
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    <strong>{l.descricao}</strong> será excluído
                                    permanentemente.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>
                                    Cancelar
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-destructive hover:bg-destructive/90"
                                    onClick={() =>
                                      deleteLancamentoMutation.mutate(l.id)
                                    }
                                  >
                                    Excluir
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Preços da Plataforma ── */}
        {isOwner && (
          <TabsContent value="precos">
            <PrecosPlataformaPanel session={session} />
          </TabsContent>
        )}

        {/* ── Taxas Asaas ── */}
        {isOwner && (
          <TabsContent value="taxas-asaas">
            <TaxasAsaasPanel />
          </TabsContent>
        )}
      </Tabs>
    </Shell>
  );
}

// ─── PrecosPlataformaPanel ────────────────────────────────────────────────────

const TIPOS_VEICULO = [
  { key: "auto_leve", label: "🚗 Auto Leve" },
  { key: "moto", label: "🏍️ Moto" },
  { key: "caminhao", label: "🚛 Caminhão" },
  { key: "onibus", label: "🚌 Ônibus" },
];

function PrecosPlataformaPanel({ session }: { session: any }) {
  const { toast } = useToast();
  const [precos, setPrecos] = useState<Record<string, string>>({});
  const [loaded, setLoaded] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["precos-plataforma"],
    queryFn: async () => {
      const res = await fetch("/api/financeiro/precos-plataforma", {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      return res.json() as Promise<{ tipo_veiculo: string; valor: string }[]>;
    },
  });

  // Load into state once
  if (data && !loaded) {
    const map: Record<string, string> = {};
    for (const row of data) map[row.tipo_veiculo] = row.valor;
    setPrecos(map);
    setLoaded(true);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/financeiro/precos-plataforma", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          precos: TIPOS_VEICULO.map((t) => ({
            tipo_veiculo: t.key,
            valor: parseFloat(precos[t.key] ?? "0"),
          })),
        }),
      });
      if (!res.ok) throw new Error("Erro ao salvar preços");
    },
    onSuccess: () => {
      refetch();
      toast({ title: "Preços da plataforma salvos!" });
    },
    onError: (err: Error) => {
      toast({
        title: "Erro",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  if (isLoading) return <Skeleton className="h-40 w-full rounded-xl" />;

  return (
    <Card className="border-none shadow-md">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Preços da Plataforma</CardTitle>
        <p className="text-xs text-muted-foreground">
          Valor cobrado da Vistoriadora por laudo finalizado, por tipo de
          veículo.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {TIPOS_VEICULO.map((t) => (
          <div key={t.key} className="flex items-center gap-4">
            <span className="w-36 text-sm">{t.label}</span>
            <div className="flex items-center gap-1">
              <span className="text-sm text-muted-foreground">R$</span>
              <Input
                type="number"
                step="0.01"
                min="0"
                className="w-28"
                value={precos[t.key] ?? "0"}
                onChange={(e) =>
                  setPrecos((prev) => ({ ...prev, [t.key]: e.target.value }))
                }
              />
            </div>
          </div>
        ))}
        <div className="flex justify-end pt-2">
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            Salvar Preços
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
